// components/admin/domain/DnsManagement.js
import React, { useState, useEffect, useMemo } from 'react';
import { Clock, Copy, CheckCircle2 } from 'lucide-react';
import Modal from '../../ui/Modal';
import Button from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import axiosInstance from '../../api/axiosInstance';
import { useNotification } from '../../../context/NotificationContext';
import logger from '../../../utils/logger';

const DnsManagement = ({
  open,
  onClose,
  domainName,
  dnsRecords: initialDnsRecords,
  verificationToken,
  domainId,
  isNewDomain,
  onVerificationComplete
}) => {
  const notify = useNotification();
  const [dnsRecords, setDnsRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState('pending');

  // Fetch DNS records on component mount
  useEffect(() => {
    const fetchDnsRecords = async () => {
      try {
        setIsLoading(true);
        const response = await axiosInstance.get(`/api/business/domains/${domainId}/dns`);

        if (response.data.success) {
          setDnsRecords(response.data.dnsRecords);
        }
      } catch (error) {
        logger.error('Error fetching DNS records:', error);
        notify('Failed to fetch DNS records', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (domainId && !isNewDomain) {
      fetchDnsRecords();
    } else {
      setDnsRecords(initialDnsRecords || []);
      setIsLoading(false);
    }
  }, [domainId, isNewDomain, initialDnsRecords]);



  // Required DNS records for setup
  const requiredRecords = useMemo(() => ([
    {
      type: 'TXT',
      host: '@',
      value: `aiinak-verify=${verificationToken}`,
      purpose: 'Domain Verification'
    },
    {
      type: 'MX',
      host: '@',
      value: 'mail.aiinak.com',
      priority: '10',
      purpose: 'Mail Server Record'
    },
    {
      type: 'TXT',
      host: '@',
      value: 'v=spf1 include:aiinak.com ip4:172.105.28.214 ~all',
      purpose: 'SPF Record (Recommended)'
    },
    {
      type: 'TXT',
      host: '@',
      value: 'v=spf1 ip4:172.105.28.214 ~all',
      purpose: 'SPF Record (More Restrictive)'
    },
    {
      type: 'TXT',
      host: 'mail._domainkey',
      // Must match the server's signing key exactly (/etc/opendkim/keys/aiinak.com/mail.txt).
      // This value previously contained a stray SPACE inside the base64 key
      // ("...N0ySST dZwry5..."), which every DNS provider either rejects or
      // stores verbatim — producing a key that can never validate a signature.
      // Do NOT reformat or line-wrap this string.
      value: 'v=DKIM1; h=sha256; k=rsa; p=MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAzA4mfeebldOQYLbd0xbDBX77wqM6N04a6X6BMpJ4m5fp1ptm5kIX9n1WqyDTqdyN1K8qrb3daudgmi8p0hwMO+2/aGP7e/LpoNc4ujWRcEgVehUcDlKAKsDRFCxg80//wVATDblm6isL0Xo1XmWKI5hJ0dpSnV8XNe7adN7TKoPao1sZnhemXuoDjzpA1XSAOjna20vr1HOu+y8O3Id9+ijv4gWotavHrEUAMUu0RZ6m2RUnwnc8IhqQnEtf1XIGb/XZScmzoPEl1RZN0ySSTdZwry5dypIYp2LpBZVJSIiiOEvDc/gss5jW19Ek1CKh8XblCSkRi2zp8ddyEhUMaQIDAQAB',
      purpose: 'DKIM Record (Authentication)'
    },
    {
      type: 'TXT',
      host: '_dmarc',
      value: 'v=DMARC1; p=none; rua=mailto:dmarc@aiinak.com',
      purpose: 'DMARC Record (Optional)'
    }
  ]), [verificationToken]);


  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    // Show success toast
  };

  const [isVerifying, setIsVerifying] = useState(false);

  const verifyDomain = async () => {
    try {
      setIsVerifying(true);
      const response = await axiosInstance.post(`/api/business/domains/${domainId}/verify`);

      if (response.data.success) {
        setVerificationStatus('verified');
        notify('Domain verified successfully', 'success');
        onVerificationComplete?.();
      } else {
        notify(response.data.message || 'Domain verification failed', 'error');
      }
    } catch (error) {
      logger.error('Error verifying domain:', error);
      notify('Failed to verify domain. Please ensure DNS records are properly configured.', 'error');
    } finally {
      setIsVerifying(false);
    }
  };

  const headerContent = (
    <div className="flex items-center justify-between px-6 py-4 -mx-6 -mt-4 mb-4 border-b border-border bg-primary/5">
      <div>
        <h3 className="text-lg font-semibold mb-0.5 text-primary">
          DNS Management
        </h3>
        <p className="text-sm text-muted-foreground">
          {domainName}
        </p>
      </div>
      <Badge variant={verificationStatus === 'verified' ? 'success' : 'warning'}>
        {verificationStatus === 'verified' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
        {verificationStatus === 'verified' ? 'Verified' : 'Pending Verification'}
      </Badge>
    </div>
  );

  const footer = (
    <div className="flex justify-end">
      <Button
        variant="primary"
        onClick={verifyDomain}
        disabled={isVerifying}
        loading={isVerifying}
        icon={<CheckCircle2 />}
      >
        {isVerifying ? 'Verifying...' : 'Verify DNS Records'}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      size="xl"
      footer={footer}
    >
      {headerContent}

      {/* Required DNS Records Section */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4 text-primary">
          Required DNS Records
        </h3>

        {requiredRecords.map((record, index) => (
          <div
            key={index}
            className="mb-4 p-4 border border-primary/10 rounded-lg hover:bg-primary/5 transition-colors"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-primary">
                {record.purpose}
              </p>
              <button
                onClick={() => handleCopy(record.value)}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-sm text-primary hover:bg-primary/10 transition-colors"
              >
                <Copy className="w-3.5 h-3.5" />
                Copy Value
              </button>
            </div>

            <div className="grid grid-cols-12 gap-4 items-start">
              <div className="col-span-2">
                <Badge>{record.type}</Badge>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-foreground">{record.host}</p>
              </div>
              <div className={record.priority ? 'col-span-6' : 'col-span-8'}>
                <p className="text-sm break-all font-mono bg-muted/50 p-2 rounded">
                  {record.value}
                </p>
              </div>
              {record.priority && (
                <div className="col-span-2">
                  <p className="text-sm text-foreground">{record.priority}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Current DNS Records Section */}
      {!isNewDomain && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-primary">
            Current DNS Records
          </h3>

          {isLoading ? (
            <div className="flex justify-center py-6">
              <p className="text-sm text-muted-foreground">Loading DNS records...</p>
            </div>
          ) : dnsRecords && dnsRecords.length > 0 ? (
            dnsRecords.map((record, index) => (
              <div
                key={index}
                className="mb-4 p-4 border border-success/20 bg-success/10 rounded-lg"
              >
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-2">
                    <Badge variant="success">{record.type}</Badge>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm font-medium text-foreground">
                      {record.name || record.host || '@'}
                    </p>
                  </div>
                  <div className={record.priority ? 'col-span-6' : 'col-span-8'}>
                    <p className="text-sm break-all font-mono bg-card/80 p-2 rounded">
                      {record.content || record.value}
                    </p>
                  </div>
                  {record.priority && (
                    <div className="col-span-2">
                      <p className="text-sm text-foreground">{record.priority}</p>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center bg-muted/30 rounded-lg border border-dashed border-border">
              <p className="text-sm text-muted-foreground">
                No existing DNS records found
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default DnsManagement;
